import React, { useState } from 'react';
import { Type, Barcode, QrCode, Plus, Hash, Square, Minus, Pin, PinOff, Calendar, Clock } from 'lucide-react';
import { ElementType, CalculatedVariable } from './types';
import { CalculatedVariablesModal } from './CalculatedVariablesModal';

interface LeftPanelProps {
  onAddElement: (type: ElementType, initialContent?: string) => void;
  variables: any[];
  onAddVariable: (variable: string) => void;
  selectedElementId: string | null;
  isPinned?: boolean;
  onTogglePin?: () => void;
  calculatedVariables?: any[];
  onSaveCalculatedVariable?: (v: any) => void;
}

export const LeftPanel: React.FC<LeftPanelProps> = ({
  onAddElement,
  variables,
  onAddVariable,
  selectedElementId,
  isPinned = false,
  onTogglePin,
  calculatedVariables = [],
  onSaveCalculatedVariable
}) => {
  const [showVarModal, setShowVarModal] = useState(false);

  return (
    <div className={`w-full h-full flex flex-col bg-card p-3 rounded-lg border border-borderColor shadow-sm overflow-y-auto custom-scrollbar transition-all duration-300 relative group`}>

      {/* Absolute Pin Button */}
      {onTogglePin && (
        <div className="absolute top-3 left-4 z-10">
          <button
            onClick={onTogglePin}
            className={`p-1.5 rounded hover:bg-bgAlt transition-colors ${isPinned ? 'text-text hover:text-text/70' : 'text-text/50 hover:text-text'}`}
            title={isPinned ? 'Sabitlemeyi Kaldır' : 'Sabitle'}
          >
            {isPinned ? <PinOff className="w-5 h-5" /> : <Pin className="w-5 h-5" />}
          </button>
        </div>
      )}

      <div className="w-[250px] flex flex-col gap-4 min-h-min">
        <div className="shrink-0 mt-8">
          <div className={`flex items-center mb-3 transition-opacity ${isPinned ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
            <h2 className="text-sm font-bold text-text uppercase tracking-wider pl-12 whitespace-nowrap">Araçlar</h2>
          </div>
          <div className="flex flex-col gap-2">
            <button
              onClick={() => onAddElement('TEXT')}
              className="flex items-center gap-3 w-full p-2 text-left text-sm font-medium text-text hover:bg-primary/10 hover:text-primary rounded-md transition-colors"
              title="Metin Ekle"
            >
              <Type className="w-5 h-5 shrink-0" />
              <span className={`whitespace-nowrap transition-opacity ${isPinned ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>Metin Ekle</span>
            </button>
            <button
              onClick={() => onAddElement('BARCODE')}
              className="flex items-center gap-3 w-full p-2 text-left text-sm font-medium text-text hover:bg-primary/10 hover:text-primary rounded-md transition-colors"
              title="Barkod Ekle"
            >
              <Barcode className="w-5 h-5 shrink-0" />
              <span className={`whitespace-nowrap transition-opacity ${isPinned ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>Barkod Ekle</span>
            </button>
            <button
              onClick={() => onAddElement('QR')}
              className="flex items-center gap-3 w-full p-2 text-left text-sm font-medium text-text hover:bg-primary/10 hover:text-primary rounded-md transition-colors"
              title="QR Kod Ekle"
            >
              <QrCode className="w-5 h-5 shrink-0" />
              <span className={`whitespace-nowrap transition-opacity ${isPinned ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>QR Kod Ekle</span>
            </button>
            <button
              onClick={() => onAddElement('BOX')}
              className="flex items-center gap-3 w-full p-2 text-left text-sm font-medium text-text hover:bg-primary/10 hover:text-primary rounded-md transition-colors"
              title="Kutu Ekle"
            >
              <Square className="w-5 h-5 shrink-0" />
              <span className={`whitespace-nowrap transition-opacity ${isPinned ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>Kutu Ekle</span>
            </button>
            <button
              onClick={() => onAddElement('LINE')}
              className="flex items-center gap-3 w-full p-2 text-left text-sm font-medium text-text hover:bg-primary/10 hover:text-primary rounded-md transition-colors"
              title="Yatay Çizgi Ekle"
            >
              <Minus className="w-5 h-5 shrink-0" />
              <span className={`whitespace-nowrap transition-opacity ${isPinned ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>Yatay Çizgi Ekle</span>
            </button>
            <button
              onClick={() => onAddElement('VLINE')}
              className="flex items-center gap-3 w-full p-2 text-left text-sm font-medium text-text hover:bg-primary/10 hover:text-primary rounded-md transition-colors"
              title="Dikey Çizgi Ekle"
            >
              <Minus className="w-5 h-5 shrink-0 rotate-90" />
              <span className={`whitespace-nowrap transition-opacity ${isPinned ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>Dikey Çizgi Ekle</span>
            </button>
            <button
              onClick={() => onAddElement('LIST')}
              className="flex items-center gap-3 w-full p-2 text-left text-sm font-medium text-text hover:bg-primary/10 hover:text-primary rounded-md transition-colors"
              title="Liste/Tablo Ekle"
            >
              <Hash className="w-5 h-5 shrink-0" />
              <span className={`whitespace-nowrap transition-opacity ${isPinned ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>Liste/Tablo Ekle</span>
            </button>
            <button
              onClick={() => onAddElement('TEXT', '{{TARIH}}')}
              className="flex items-center gap-3 w-full p-2 text-left text-sm font-medium text-text hover:bg-primary/10 hover:text-primary rounded-md transition-colors"
              title="Tarih Ekle"
            >
              <Calendar className="w-5 h-5 shrink-0" />
              <span className={`whitespace-nowrap transition-opacity ${isPinned ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>Tarih Ekle</span>
            </button>
            <button
              onClick={() => onAddElement('TEXT', '{{SAAT}}')}
              className="flex items-center gap-3 w-full p-2 text-left text-sm font-medium text-text hover:bg-primary/10 hover:text-primary rounded-md transition-colors"
              title="Saat Ekle"
            >
              <Clock className="w-5 h-5 shrink-0" />
              <span className={`whitespace-nowrap transition-opacity ${isPinned ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>Saat Ekle</span>
            </button>
          </div>
        </div>

        <div className={`border-t border-borderColor my-2 w-[80%] transition-opacity shrink-0 ${isPinned ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}></div>

        <div className={`flex flex-col shrink-0 transition-opacity ${isPinned ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
          <div className="flex items-center justify-between mb-3 pr-2 shrink-0">
            <h2 className="text-sm font-bold text-text uppercase tracking-wider pl-1">Dinamik Değişkenler</h2>
            <button 
              onClick={() => setShowVarModal(true)}
              className="text-[10px] bg-primary/10 text-primary px-2 py-1 rounded font-bold hover:bg-primary/20 transition-colors"
            >
              + Koşullu Değişken
            </button>
          </div>
          <p className="text-xs text-text/70 mb-3 pl-1 pr-4 shrink-0">
            Bir metin, barkod veya QR kutusu seçiliyken, değişken eklemek için aşağıdaki butonlara tıklayın.
          </p>

          <div className="flex flex-col gap-2 pr-2 pb-2">
            {calculatedVariables.map((v: any, i: number) => (
              <button
                key={`calc-${i}`}
                onClick={() => onAddVariable(`{{${v.name}}}`)}
                disabled={!selectedElementId}
                className={`flex flex-col items-start px-3 py-2 text-xs rounded-md transition-colors border-y border-r border-l-4 text-left shadow-sm ${selectedElementId
                  ? 'bg-primary/5 hover:bg-primary/10 border-borderColor border-l-primary'
                  : 'bg-bgAlt border-borderColor border-l-gray-300 text-text/50 cursor-not-allowed opacity-60'
                  }`}
                title="Seçili alana ekle"
              >
                <div className="flex items-center justify-between w-full mb-1">
                  <div className="flex items-center gap-2">
                    <span className={`font-bold truncate max-w-[150px] ${selectedElementId ? 'text-text' : 'text-text/50'}`}>
                      {v.name} (Özel)
                    </span>
                  </div>
                  <Plus className={`w-3 h-3 shrink-0 ${selectedElementId ? 'text-primary' : 'text-gray-400'}`} />
                </div>
                <span className={`font-mono text-[10px] ml-5 truncate max-w-[150px] ${selectedElementId ? 'text-text/70' : 'text-text/40'}`}>
                  {`{{${v.name}}}`}
                </span>
              </button>
            ))}

            {variables.length === 0 ? (
              <p className="text-xs italic text-text/50 pl-1">Bu kategori için değişken bulunamadı veya henüz kategori seçilmedi.</p>
            ) : (
              variables.map((v, i) => {
                const varType = v.type || 'text';
                const Icon = varType === 'barcode' ? Barcode : varType === 'number' ? Hash : Type;
                const typeColor = varType === 'barcode' ? 'text-indigo-500' : varType === 'number' ? 'text-green-500' : 'text-blue-500';
                const borderLeftColor = varType === 'barcode' ? 'border-l-indigo-500' : varType === 'number' ? 'border-l-green-500' : 'border-l-blue-500';

                return (
                  <button
                    key={i}
                    onClick={() => onAddVariable(`{{${v.key || v.name || v}}}`)}
                    disabled={!selectedElementId}
                    className={`flex flex-col items-start px-3 py-2 text-xs rounded-md transition-colors border-y border-r border-l-4 text-left shadow-sm ${selectedElementId
                        ? `bg-card hover:bg-bgAlt border-borderColor ${borderLeftColor}`
                        : 'bg-bgAlt border-borderColor border-l-gray-300 text-text/50 cursor-not-allowed opacity-60'
                      }`}
                    title="Seçili alana ekle"
                  >
                    <div className="flex items-center justify-between w-full mb-1">
                      <div className="flex items-center gap-2">
                        <Icon className={`w-3.5 h-3.5 ${selectedElementId ? typeColor : 'text-gray-400'}`} />
                        <span className={`font-bold truncate max-w-[150px] ${selectedElementId ? 'text-text' : 'text-text/50'}`}>
                          {v.label || v.name || v}
                        </span>
                      </div>
                      <Plus className={`w-3 h-3 shrink-0 ${selectedElementId ? 'text-primary' : 'text-gray-400'}`} />
                    </div>
                    <span className={`font-mono text-[10px] ml-5 truncate max-w-[150px] ${selectedElementId ? 'text-text/70' : 'text-text/40'}`}>
                      {`{{${v.key || v.name || v}}}`}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      </div>
      
      {showVarModal && (
        <CalculatedVariablesModal
          visible={showVarModal}
          onHide={() => setShowVarModal(false)}
          onSave={(calcVar) => {
             if (onSaveCalculatedVariable) {
                 onSaveCalculatedVariable(calcVar);
             }
             setShowVarModal(false);
          }}
        />
      )}
    </div>
  );
};
